import { useEffect, useState } from "react";
import { api } from "../lib/api";

// The Grade Curve — a rolling 30-day line chart of the aggregate error rate.
// SVG only, no chart library. Nulls (no reviews that day) render as gaps.
export default function GradeCurve({ days = 30, refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await api.errorRateCurve(days);
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setErr(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [days, refreshKey]);

  if (err) return <div data-testid="grade-curve-error" className="text-xs text-sage-warn">{err}</div>;
  if (!data) return <div data-testid="grade-curve-loading" className="text-xs text-sage-faint">Loading curve…</div>;

  const points = data.points || [];
  const w = 640, h = 160, padL = 32, padR = 8, padT = 12, padB = 22;
  const innerW = w - padL - padR, innerH = h - padT - padB;

  const rated = points.filter((p) => p.rate !== null && p.rate !== undefined);
  const maxRate = Math.max(0.1, ...rated.map((p) => p.rate));  // floor at 10% so a clean day looks clean
  const scaleY = (r) => padT + innerH - (r / maxRate) * innerH;
  const scaleX = (i) => padL + (points.length > 1 ? (i / (points.length - 1)) * innerW : innerW / 2);

  // Build a polyline path with gaps for null days.
  let path = "";
  let started = false;
  points.forEach((p, i) => {
    if (p.rate === null || p.rate === undefined) {
      started = false;
      return;
    }
    const cmd = started ? "L" : "M";
    path += `${cmd}${scaleX(i).toFixed(1)},${scaleY(p.rate).toFixed(1)} `;
    started = true;
  });

  const gridLines = [0, 0.25, 0.5, 0.75, 1].map((f) => f * maxRate);
  const totalReviews = points.reduce((a, p) => a + (p.reviews || 0), 0);
  const totalErrors = points.reduce((a, p) => a + (p.errors || 0), 0);
  const avgRate = totalReviews ? totalErrors / totalReviews : 0;

  return (
    <div data-testid="grade-curve" className="mt-4 pt-4 border-t border-sage-border">
      <div className="flex justify-between items-baseline mb-2">
        <div className="text-[10px] uppercase tracking-[0.15em] text-sage-muted">
          The Grade Curve · rolling {data.days}d
        </div>
        <div className="text-[10px] text-sage-faint tabular-nums" data-testid="grade-curve-avg">
          avg {(avgRate * 100).toFixed(1)}% · {totalErrors}/{totalReviews}
        </div>
      </div>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className="w-full h-40 bg-sage-bg border border-sage-border"
        preserveAspectRatio="none"
        role="img"
        aria-label="Grade Curve error rate line chart"
      >
        {/* Y grid */}
        {gridLines.map((r, i) => (
          <g key={i}>
            <line
              x1={padL} x2={w - padR}
              y1={scaleY(r)} y2={scaleY(r)}
              stroke="currentColor"
              className="text-sage-border"
              strokeDasharray="2 3"
              strokeWidth="0.5"
            />
            <text
              x={padL - 4} y={scaleY(r) + 3}
              className="fill-current text-sage-faint"
              fontSize="9"
              textAnchor="end"
            >
              {(r * 100).toFixed(0)}%
            </text>
          </g>
        ))}
        {/* X labels: first + middle + last day */}
        {[0, Math.floor(points.length / 2), points.length - 1].map((i) => (
          points[i] && (
            <text
              key={`x-${i}`}
              x={scaleX(i)} y={h - 6}
              className="fill-current text-sage-faint"
              fontSize="9"
              textAnchor="middle"
            >
              {points[i].day.slice(5)}
            </text>
          )
        ))}
        {/* Line */}
        {path && (
          <path
            d={path}
            fill="none"
            stroke="currentColor"
            className="text-sage-accent"
            strokeWidth="1.75"
          />
        )}
        {/* Points */}
        {points.map((p, i) => (
          p.rate !== null && p.rate !== undefined ? (
            <circle
              key={i}
              cx={scaleX(i)}
              cy={scaleY(p.rate)}
              r="2.5"
              className="fill-current text-sage-accent"
            >
              <title>{p.day} · {(p.rate * 100).toFixed(1)}% · {p.errors}/{p.reviews}</title>
            </circle>
          ) : null
        ))}
      </svg>
      <div className="text-[10px] text-sage-faint mt-2 leading-relaxed">
        {data.definition} Gaps are days with zero reviews.
      </div>
    </div>
  );
}
